import type { ThemeOptions } from "@mui/material";
import { alpha } from "@mui/material";
import { darken } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

export const BADGE_COLOR = "#2A3146";
export const BADGE_TEXT_COLOR = "#F7F8FB";

export const COLORS = {
  primary: {
    100: "#CFFAFE",
    200: "#A5F3FC",
    300: "#67E8F9",
    400: "#22D3EE",
    500: "#06B6D4",
    600: "#0891B2",
    700: "#0E7490",
    800: "#155E75",
    900: "#164E63",
  },
  grey: {
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#0a1628",
    900: "#060d1a",
  },
  success: {
    100: "#E1F7EA",
    200: "#B6EECB",
    300: "#77E0A1",
    400: "#2ECC71",
    500: "#24B25F",
    600: "#1E9450",
    700: "#187741",
    800: "#125A31",
    900: "#0C3D22",
  },
  error: {
    100: "#FCE3E6",
    200: "#F8B7BF",
    300: "#F17A86",
    400: "#EF4D5B",
    500: "#E63746",
    600: "#C72F3C",
    700: "#A82633",
    800: "#891E2A",
    900: "#6B1621",
  },
} as const;

export const WARNING = {
  100: "#FEF3C7",
  200: "#FDE68A",
  300: "#FCD34D",
  400: "#FBBF24",
  500: "#F59E0B",
  600: "#D97706",
  700: "#B45309",
  800: "#92400E",
  900: "#78350F",
} as const;

export const palette = {
  mode: "dark",
  primary: COLORS.primary,
  text: { primary: COLORS.grey[100], secondary: COLORS.grey[500] },
  background: { default: "#060d1a", paper: alpha("#0F172A", 0.6) },

  // sidebar: INVARIATA
  sidebar: {
    bg: alpha("#060d1a", 0.8),
    bg2: alpha("#0F172A", 0.9),
    border: alpha(COLORS.grey[700], 0.6),
    text: alpha(COLORS.grey[300], 0.85),
    textStrong: COLORS.grey[100],
    hover: alpha(COLORS.grey[700], 0.35),
    accent: COLORS.primary[400],
    chipBg: alpha(COLORS.primary[500], 0.15),
  },

  divider: alpha(COLORS.grey[700], 0.6),
  colors: COLORS,
} as const;

const typography = {
  button: { textTransform: "none" },
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontWeightLight: "400",
  fontWeightBold: "600",
  h1: { fontSize: "3rem", lineHeight: 1.15, letterSpacing: "-0.025em" },
  h2: { fontSize: "22px", lineHeight: "27.5px", letterSpacing: "-0.025em" },
  h3: { fontSize: "18px", lineHeight: "22.5px", letterSpacing: "-0.015em" },
  h4: { fontSize: "16px", lineHeight: "20px" },
  h5: { fontSize: "14px", lineHeight: "17.5px" },
  h6: { fontSize: "12px", lineHeight: "15px", fontWeight: "400" },
  body1: { fontSize: "14px", lineHeight: "22.4px" },
  body2: { fontSize: "14px", lineHeight: "150%", color: COLORS.grey[400] },
  subtitle1: {
    fontWeight: "600",
    fontSize: "11px",
    lineHeight: "13.75px",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: palette.text.secondary,
  },
} as const;

const DD_RADIUS_SM = "6px";
const DD_RADIUS_MD = "8px";
const DD_RADIUS_LG = "10px";

const DD_BTN_RADIUS = "8px";
const DD_BTN_H_MD = "38px";
const DD_BTN_H_LG = "38px";
const ddAccent = palette.sidebar.accent;

const focusRing = (c: string) => `0 0 0 3px ${alpha(c, 0.35)}`;

const neutralText = (mode: "light" | "dark") =>
  mode === "dark" ? COLORS.grey[100] : COLORS.grey[900];
const neutralBg = (mode: "light" | "dark") =>
  mode === "dark" ? COLORS.grey[900] : "#FFFFFF";
const neutralBorder = (mode: "light" | "dark") =>
  mode === "dark" ? COLORS.grey[700] : COLORS.grey[200];
const neutralHoverBg = (mode: "light" | "dark") =>
  mode === "dark"
    ? alpha(COLORS.grey[600], 0.35)
    : alpha(COLORS.grey[200], 0.55);
const neutralActiveBg = (mode: "light" | "dark") =>
  mode === "dark"
    ? alpha(COLORS.grey[600], 0.5)
    : alpha(COLORS.grey[300], 0.65);

// dark/glass tokens
const SURFACE = COLORS.grey[900];
const SURFACE_2 = COLORS.grey[800];
const BORDER = COLORS.grey[700];
const BORDER_STRONG = COLORS.grey[600];
const TEXT = COLORS.grey[100];
const TEXT_MUTED = COLORS.grey[300];
const ICON = COLORS.grey[300];
const HOVER = COLORS.grey[700];
const ACTIVE = COLORS.grey[700];

const buttons = {
  common: { padding: "10px 14px", borderRadius: DD_RADIUS_MD },
  size: {
    large: {
      fontSize: typography.h4.fontSize,
      lineHeight: typography.h4.lineHeight,
      fontWeight: typography.fontWeightBold,
      height: "48px",
      "&& .MuiButton-startIcon": { marginRight: "12px" },
      "&& .MuiButton-startIcon svg": { fontSize: "20px" },
      "&& .MuiButton-endIcon": { marginLeft: "12px" },
      "&& .MuiButton-endIcon svg": { fontSize: "20px" },
    },
    medium: {
      fontSize: typography.h5.fontSize,
      lineHeight: typography.h5.lineHeight,
      fontWeight: typography.fontWeightBold,
      height: "40px",
      "&& .MuiButton-startIcon": { marginRight: "8px" },
      "&& .MuiButton-startIcon svg": { fontSize: "16px" },
      "&& .MuiButton-endIcon": { marginLeft: "8px" },
      "&& .MuiButton-endIcon svg": { fontSize: "16px" },
    },
  },
  variant: {
    contained: (colorset: Record<number, string>) => ({
      boxShadow: "none",
      borderRadius: DD_BTN_RADIUS,
      backgroundColor: alpha(colorset[500], 0.12),
      border: `1px solid ${alpha(colorset[400], 0.25)}`,
      color: colorset[300],
      ":hover": {
        backgroundColor: alpha(colorset[500], 0.18),
        borderColor: alpha(colorset[400], 0.35),
        boxShadow: `0 0 20px ${alpha(colorset[500], 0.08)}`,
      },
      ":active": {
        backgroundColor: alpha(colorset[500], 0.24),
        borderColor: alpha(colorset[400], 0.45),
      },
      ":focus": {
        outline: "none",
        boxShadow: focusRing(colorset[400]),
      },
      ":disabled": {
        backgroundColor: alpha(colorset[500], 0.06),
        borderColor: alpha(colorset[400], 0.12),
        color: alpha(COLORS.grey[400], 0.6),
      },
      "&.MuiLoadingButton-loading": {
        backgroundColor: alpha(colorset[500], 0.2),
      },
      "& .MuiLoadingButton-loadingIndicator": { color: colorset[300] },
    }),
    text: (color: "primary" | "secondary" | "error") => ({
      color:
        color === "primary"
          ? COLORS.primary[400]
          : color === "secondary"
            ? COLORS.grey[100]
            : COLORS.error[500],
      ":hover": { background: alpha(COLORS.grey[600], 0.25) },
      ":active": { background: alpha(COLORS.grey[600], 0.35) },
      ":focus": {
        outline: "none",
        boxShadow: `0 0 0 3px ${
          color === "primary"
            ? alpha(COLORS.primary[400], 0.35)
            : color === "error"
              ? alpha(COLORS.error[500], 0.35)
              : alpha(COLORS.grey[400], 0.35)
        }`,
      },
      ":disabled": {
        color:
          color === "primary"
            ? alpha(COLORS.primary[400], 0.35)
            : alpha(COLORS.grey[100], 0.35),
      },
      "&.MuiLoadingButton-loading": {
        backgroundColor: alpha(COLORS.grey[600], 0.2),
      },
      "& .MuiLoadingButton-loadingIndicator": {
        color: COLORS.primary[400],
      },
    }),
    outlined: {
      color: TEXT,
      backgroundColor: "transparent",
      border: `1px solid ${BORDER}`,
      borderRadius: DD_BTN_RADIUS,
      ":hover": {
        backgroundColor: alpha(COLORS.grey[800], 0.35),
        borderColor: alpha(COLORS.grey[600], 0.6),
      },
      ":active": {
        backgroundColor: alpha(COLORS.grey[800], 0.5),
        boxShadow: focusRing(COLORS.grey[500]),
      },
      ":focus": {
        outline: "none",
        borderColor: alpha(COLORS.primary[400], 0.5),
        boxShadow: focusRing(COLORS.primary[400]),
      },
      ":disabled": {
        border: `1px solid ${alpha(COLORS.grey[700], 0.4)}`,
        color: alpha(COLORS.grey[500], 0.7),
      },
      "&.MuiLoadingButton-loading": {
        border: `1px solid ${alpha(COLORS.primary[400], 0.35)}`,
        backgroundColor: alpha(COLORS.primary[500], 0.08),
      },
      "& .MuiLoadingButton-loadingIndicator": {
        color: COLORS.primary[300],
      },
    },
  },
} as const;

const breakpoints = { xs: 0, sm: 600, md: 1000, lg: 1200, xl: 1920 };

export const themeOptions: ThemeOptions = {
  palette,
  breakpoints: { values: breakpoints },
  typography,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          backgroundImage:
            "radial-gradient(800px circle at 20% 10%, rgba(6, 182, 212, 0.10), transparent 55%), radial-gradient(700px circle at 80% 0%, rgba(6, 182, 212, 0.06), transparent 60%)",
        },
        "code, pre, kbd, samp": {
          fontFamily:
            '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        },
        "*": {
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(COLORS.grey[500], 0.35)} transparent`,
        },
        "*::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },
        "*::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          background: alpha(COLORS.grey[500], 0.35),
          borderRadius: "3px",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          background: alpha(COLORS.grey[500], 0.55),
        },
        "::selection": {
          background: alpha(COLORS.primary[500], 0.3),
          color: COLORS.grey[100],
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { margin: 0 },
      },
    },

    MuiCheckbox: {
      defaultProps: { disableFocusRipple: true, disableRipple: true },
      styleOverrides: {
        root: {
          "@media (hover: hover)": {
            ":hover": { color: COLORS.primary[400] },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: DD_RADIUS_LG,
          padding: 10,
          background: SURFACE_2,
          backgroundImage: "none",
          backdropFilter: "blur(12px)",
          border: `1px solid ${BORDER}`,
        },
      },
    },
    MuiAccordion: {
      defaultProps: { defaultExpanded: false },
      styleOverrides: {
        root: {
          overflow: "hidden",
          "&.MuiPaper-rounded": { margin: "0 -16px" },
          "&.MuiPaper-elevation": { borderRadius: DD_RADIUS_LG },
          border: `1px solid ${BORDER}`,
          backgroundColor: SURFACE,
          [`@media (min-width: ${breakpoints.sm}px)`]: {
            "&.MuiPaper-rounded": { borderRadius: DD_RADIUS_LG, margin: 0 },
          },
        },
      },
    },

    MuiAccordionSummary: {
      defaultProps: {},
      styleOverrides: {
        root: { "&.Mui-expanded": { minHeight: "auto" } },
        content: { margin: "8px 0", "&.Mui-expanded": { margin: "8px 0" } },
        expandIconWrapper: {},
      },
    },

    MuiAccordionDetails: {},
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:focus": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: DD_RADIUS_MD,
          alignItems: "center",
          border: `1px solid ${BORDER}`,
          backgroundColor: SURFACE,
          color: TEXT,
        },
        icon: { marginRight: "16px", fontSize: "24px", padding: 0 },
        message: { padding: 0 },
      },
      variants: [
        {
          props: { variant: "outlined", severity: "info" },
          style: {
            backgroundColor: alpha(COLORS.primary[500], 0.06),
            borderColor: alpha(COLORS.primary[400], 0.35),
            color: TEXT,
            "& .MuiAlert-icon": { color: COLORS.primary[400] },
          },
        },
        {
          props: { variant: "filled", severity: "warning" },
          style: {
            backgroundColor: alpha(COLORS.error[500], 0.06),
            borderColor: alpha(COLORS.error[500], 0.35),
            color: TEXT,
            "& .MuiAlert-icon": { color: COLORS.error[500] },
          },
        },
        {
          props: { variant: "filled", severity: "info" },
          style: {
            backgroundColor: alpha(COLORS.primary[500], 0.06),
            borderColor: alpha(COLORS.primary[400], 0.35),
            color: TEXT,
            "& .MuiAlert-icon": { color: COLORS.primary[400] },
          },
        },
        {
          props: { variant: "outlined", severity: "error" },
          style: {
            backgroundColor: alpha(COLORS.error[500], 0.06),
            borderColor: alpha(COLORS.error[500], 0.35),
            color: TEXT,
            "& .MuiAlert-icon": { color: COLORS.error[500] },
          },
        },
      ],
    },

    MuiAlertTitle: { styleOverrides: { root: { fontSize: "16px" } } },

    MuiButton: {
      defaultProps: {
        disableFocusRipple: true,
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: DD_BTN_RADIUS,
          boxShadow: "none",
          fontWeight: 800,
          minWidth: "unset",
        },
      },
      variants: [
        {
          props: {},
          style: {
            height: DD_BTN_H_MD,
            padding: "0 12px",
            fontSize: "13px",
            lineHeight: "16px",
            "&& .MuiButton-startIcon": { marginRight: "8px" },
            "&& .MuiButton-startIcon svg": { fontSize: "16px" },
            "&& .MuiButton-endIcon": { marginLeft: "8px" },
            "&& .MuiButton-endIcon svg": { fontSize: "16px" },
          },
        },
        {
          props: { size: "large" },
          style: {
            height: DD_BTN_H_LG,
            padding: "0 14px",
            fontSize: "14px",
            lineHeight: "18px",
            "&& .MuiButton-startIcon": { marginRight: "10px" },
            "&& .MuiButton-startIcon svg": { fontSize: "18px" },
            "&& .MuiButton-endIcon": { marginLeft: "10px" },
            "&& .MuiButton-endIcon svg": { fontSize: "18px" },
          },
        },

        {
          props: { variant: "contained", color: "primary" },
          style: {
            backgroundColor: ddAccent,
            color: COLORS.grey[100],
            border: "1px solid transparent",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: darken(ddAccent, 0.08),
              boxShadow: "none",
            },
            "&:active": {
              backgroundColor: darken(ddAccent, 0.14),
              boxShadow: "none",
            },
            "&:focus-visible": {
              outline: "none",
              boxShadow: focusRing(ddAccent),
            },
            "&.Mui-disabled": {
              backgroundColor: alpha(ddAccent, 0.35),
              color: alpha(COLORS.grey[100], 0.65),
            },
            "&.MuiLoadingButton-loading": {
              color: "transparent",
              backgroundColor: darken(ddAccent, 0.08),
            },
            "& .MuiLoadingButton-loadingIndicator": { color: COLORS.grey[100] },
          },
        },

        {
          props: { variant: "contained", color: "secondary" },
          style: ({ theme }: { theme: Theme }) => {
            const mode = theme.palette.mode as "light" | "dark";
            const b = neutralBorder(mode);
            return {
              backgroundColor: neutralBg(mode),
              color: neutralText(mode),
              border: `1px solid ${b}`,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: neutralHoverBg(mode),
                borderColor: b,
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: neutralActiveBg(mode),
                borderColor: b,
                boxShadow: "none",
              },
              "&:focus-visible": {
                outline: "none",
                borderColor: ddAccent,
                boxShadow: focusRing(ddAccent),
              },
              "&.Mui-disabled": {
                opacity: 0.55,
                backgroundColor: neutralBg(mode),
                color: neutralText(mode),
                borderColor: b,
              },
              "&.MuiLoadingButton-loading": {
                color: "transparent",
                backgroundColor: neutralHoverBg(mode),
              },
              "& .MuiLoadingButton-loadingIndicator": { color: ddAccent },
            };
          },
        },

        {
          props: { variant: "contained", color: "info" },
          style: {
            backgroundColor: SURFACE_2,
            color: TEXT,
            border: `1px solid ${BORDER}`,
            boxShadow: "none",
            ":hover": { backgroundColor: alpha(COLORS.grey[200], 0.9) },
            ":active": { backgroundColor: alpha(COLORS.grey[300], 0.9) },
            ":focus-visible": {
              outline: "none",
              boxShadow: focusRing(ddAccent),
            },
            ":disabled": {
              backgroundColor: alpha(COLORS.grey[200], 0.55),
              color: alpha(TEXT, 0.45),
              borderColor: alpha(BORDER, 0.65),
            },
          },
        },

        {
          props: { variant: "contained", color: "error" },
          style: {
            backgroundColor: COLORS.error[500],
            color: COLORS.grey[100],
            border: "1px solid transparent",
            boxShadow: "none",
            ":hover": { backgroundColor: COLORS.error[600] },
            ":active": { backgroundColor: COLORS.error[700] },
            ":focus-visible": {
              outline: "none",
              boxShadow: focusRing(COLORS.error[500]),
            },
            ":disabled": {
              backgroundColor: alpha(COLORS.error[500], 0.35),
              color: alpha(COLORS.grey[100], 0.35),
            },
          },
        },

        {
          props: { variant: "outlined" },
          style: ({ theme }: { theme: Theme }) => {
            const mode = theme.palette.mode as "light" | "dark";
            const b = neutralBorder(mode);
            return {
              backgroundColor: "transparent",
              color: neutralText(mode),
              border: `1px solid ${b}`,
              borderRadius: DD_BTN_RADIUS,
              "&:hover": {
                backgroundColor: neutralHoverBg(mode),
                borderColor: b,
              },
              "&:active": {
                backgroundColor: neutralActiveBg(mode),
                borderColor: b,
              },
              "&:focus-visible": {
                outline: "none",
                borderColor: ddAccent,
                boxShadow: focusRing(ddAccent),
              },
              "&.Mui-disabled": {
                opacity: 0.55,
                borderColor: b,
                color: neutralText(mode),
              },
              "&.MuiLoadingButton-loading": {
                backgroundColor: neutralHoverBg(mode),
              },
              "& .MuiLoadingButton-loadingIndicator": { color: ddAccent },
            };
          },
        },

        {
          props: { variant: "outlined", color: "primary" },
          style: {
            backgroundColor: alpha(COLORS.primary[500], 0.1),
            borderColor: alpha(COLORS.primary[500], 0.2),
            color: COLORS.primary[400],
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(COLORS.primary[500], 0.2),
              borderColor: alpha(COLORS.primary[500], 0.3),
              boxShadow: `0 0 20px ${alpha(COLORS.primary[500], 0.05)}`,
            },
            "&:active": {
              backgroundColor: alpha(COLORS.primary[500], 0.28),
              borderColor: alpha(COLORS.primary[500], 0.35),
            },
            "&:focus-visible": {
              outline: "none",
              borderColor: alpha(COLORS.primary[500], 0.35),
              boxShadow: focusRing(COLORS.primary[400]),
            },
          },
        },

        {
          props: { variant: "outlined", color: "error" },
          style: {
            backgroundColor: alpha(COLORS.error[500], 0.1),
            borderColor: alpha(COLORS.error[500], 0.2),
            color: COLORS.error[500],
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(COLORS.error[500], 0.16),
              borderColor: alpha(COLORS.error[500], 0.3),
              boxShadow: `0 0 20px ${alpha(COLORS.error[500], 0.05)}`,
            },
            "&:active": {
              backgroundColor: alpha(COLORS.error[500], 0.24),
              borderColor: alpha(COLORS.error[500], 0.35),
            },
            "&:focus-visible": {
              outline: "none",
              borderColor: alpha(COLORS.error[500], 0.35),
              boxShadow: focusRing(COLORS.error[500]),
            },
          },
        },

        {
          props: { variant: "outlined", color: "warning" },
          style: {
            backgroundColor: alpha(WARNING[500], 0.1),
            borderColor: alpha(WARNING[500], 0.2),
            color: WARNING[400],
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(WARNING[500], 0.16),
              borderColor: alpha(WARNING[500], 0.3),
              boxShadow: `0 0 15px ${alpha(WARNING[500], 0.08)}`,
            },
            "&:active": {
              backgroundColor: alpha(WARNING[500], 0.24),
              borderColor: alpha(WARNING[500], 0.35),
            },
            "&:focus-visible": {
              outline: "none",
              borderColor: alpha(WARNING[500], 0.35),
              boxShadow: focusRing(WARNING[500]),
            },
          },
        },

        {
          props: { variant: "text" },
          style: {
            color: ddAccent,
            "&:hover": { backgroundColor: alpha(ddAccent, 0.08) },
            "&:active": { backgroundColor: alpha(ddAccent, 0.14) },
            "&:focus-visible": {
              outline: "none",
              boxShadow: focusRing(ddAccent),
            },
            "&.Mui-disabled": { color: alpha(ddAccent, 0.35) },
          },
        },

        // utile per sidebar (invariato)
        {
          props: { variant: "text", color: "secondary" },
          style: {
            color: COLORS.grey[100],
            ":hover": { background: alpha(COLORS.grey[600], 0.25) },
            ":active": { background: alpha(COLORS.grey[600], 0.35) },
            ":focus-visible": {
              outline: "none",
              boxShadow: focusRing(ddAccent),
            },
            ":disabled": { color: alpha(COLORS.grey[100], 0.35) },
          },
        },

        {
          props: { variant: "text", color: "error" },
          style: {
            color: COLORS.error[500],
            ":hover": { background: alpha(COLORS.error[500], 0.08) },
            ":active": { background: alpha(COLORS.error[500], 0.14) },
            ":focus-visible": {
              outline: "none",
              boxShadow: focusRing(COLORS.error[500]),
            },
            ":disabled": { color: alpha(COLORS.error[500], 0.35) },
          },
        },
      ],
    },

    MuiToolbar: {
      styleOverrides: {
        regular: {
          borderBottom: `1px solid ${BORDER}`,
          backgroundColor: SURFACE,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        wrapper: { padding: "0px 16px" },
      },
      variants: [
        {
          props: { variant: "gradient" },
          style: {
            borderImage: `linear-gradient(90deg, rgba(42, 49, 70, 0) 0%, ${alpha(
              COLORS.grey[300],
              0.55,
            )} 52.08%, rgba(42, 49, 70, 0) 100%)`,
            borderImageSlice: 1,
            "&:before": {
              borderTop: "1px solid",
              borderImage: `linear-gradient(-90deg, ${BORDER} 68%, ${palette.background.paper} 100%)`,
              borderImageSlice: 1,
            },
            "&:after": {
              borderTop: "1px solid",
              borderImage: `linear-gradient(90deg, ${BORDER} 68%, ${palette.background.paper} 100%)`,
              borderImageSlice: 1,
            },
          },
        },
      ],
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${COLORS.grey[700]}`,
          borderRadius: "0px",
          backgroundColor: COLORS.grey[900],
        },
      },
    },

    MuiChip: {
      variants: [
        {
          props: { color: "success" },
          style: {
            height: "26px",
            fontSize: typography.h5.fontSize,
            lineHeight: typography.h5.lineHeight,
            backgroundColor: alpha(COLORS.success[500], 0.12),
            color: COLORS.success[300],
            border: `1px solid ${alpha(COLORS.success[500], 0.25)}`,
            borderRadius: DD_RADIUS_SM,
          },
        },
        {
          props: { color: "error" },
          style: {
            height: "26px",
            fontSize: typography.h5.fontSize,
            lineHeight: typography.h5.lineHeight,
            backgroundColor: alpha(COLORS.error[500], 0.12),
            color: COLORS.error[300],
            border: `1px solid ${alpha(COLORS.error[500], 0.25)}`,
            borderRadius: DD_RADIUS_SM,
          },
        },
        {
          props: { color: "primary" },
          style: {
            height: "26px",
            fontSize: typography.h5.fontSize,
            lineHeight: typography.h5.lineHeight,
            color: COLORS.primary[300],
            backgroundColor: alpha(COLORS.primary[400], 0.12),
            borderRadius: "999px",
            border: `1px solid ${alpha(COLORS.primary[400], 0.25)}`,
            ":hover": {
              cursor: "pointer",
              backgroundColor: alpha(COLORS.primary[500], 0.16),
            },
            ".MuiChip-deleteIcon": {
              fontSize: "20px",
              color: COLORS.primary[700],
              margin: "0px 14px 0px -2px",
              ":hover": { color: COLORS.primary[700] },
            },
          },
        },
      ],
    },

    MuiTable: {},
    MuiTableHead: {},
    MuiTableBody: {},
    MuiTableRow: {},
    MuiTableSortLabel: {},
    MuiTableCell: {},

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": { padding: 0 },
          "&& .MuiOutlinedInput-input": { padding: "10px 12px" },
        },
        paper: {
          backgroundColor: SURFACE,
          border: `1px solid ${BORDER_STRONG}`,
          borderRadius: DD_RADIUS_MD,
          padding: 0,
        },
        groupLabel: {
          height: "48px",
          padding: "14px 0px",
          backgroundColor: SURFACE_2,
          fontSize: typography.h5.fontSize,
          lineHeight: typography.h5.lineHeight,
          textTransform: "uppercase",
          borderBottom: `1px solid ${BORDER}`,
          color: TEXT_MUTED,
        },
        listbox: {
          padding: "0 !important",
          paddingLeft: 0,
          paddingRight: 0,
          ".MuiAutocomplete-option": {
            "&:hover, &:focus, &.Mui-focusVisible": { backgroundColor: HOVER },
            "&:active": { backgroundColor: ACTIVE },
            "&:active, &:focus, &.Mui-focusVisible": {
              boxShadow: `inset 0 0 0 3px ${alpha(COLORS.primary[400], 0.25)}`,
            },
            "&.Mui-focusVisible[aria-selected='true']": {
              backgroundColor: alpha(COLORS.primary[500], 0.12),
              color: TEXT,
              boxShadow: `inset 0 0 0 3px ${alpha(COLORS.primary[400], 0.25)}`,
            },
            '&[aria-selected="true"]:not(.Mui-focusVisible)': {
              backgroundColor: alpha(COLORS.primary[500], 0.12),
              color: TEXT,
              "&.Mui-focused": {
                backgroundColor: alpha(COLORS.primary[500], 0.12),
                color: TEXT,
              },
            },
            "&.Mui-disabled": {
              color: TEXT_MUTED,
              WebkitTextFillColor: TEXT_MUTED,
              opacity: 0.5,
            },
          },
        },
        option: {
          padding: "14px 16px !important",
          fontSize: typography.h4.fontSize,
          lineHeight: typography.h4.lineHeight,
        },
        popupIndicator: {
          color: ICON,
          "> *": { fontSize: "20px" },
        },
        popper: { padding: "4px 0px 4px 0px" },
        endAdornment: {
          top: "calc(50% - 18px)",
          "& .MuiTouchRipple-root": { display: "none" },
          "& .MuiIconButton-root": { padding: 0, margin: 0 },
        },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          display: "flex",
          height: "auto",
          borderRadius: "999px",
          border: `1px solid ${BORDER}`,
          backgroundColor: SURFACE_2,
          padding: "6px",
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          flex: "1",
          height: "auto",
          fontSize: typography.h4.fontSize,
          lineHeight: typography.h4.lineHeight,
          color: TEXT,
          border: "none",
          borderRadius: "999px",
          "&&.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "999px",
          },
          "&&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderRadius: "999px",
          },
          "&.Mui-selected": {
            "&:hover": { backgroundColor: alpha(COLORS.primary[500], 0.14) },
            backgroundColor: alpha(COLORS.primary[500], 0.14),
            color: COLORS.primary[300],
          },
          "&.Mui-disabled:not(.Mui-selected)": {
            color: COLORS.grey[400],
            opacity: 0.8,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: SURFACE,
          borderRadius: DD_RADIUS_MD,
          color: TEXT,
          fontSize: typography.h4.fontSize,
          lineHeight: typography.h4.lineHeight,
          "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
          "& .MuiOutlinedInput-notchedOutline": {
            top: 0,
            border: `1px solid ${BORDER_STRONG}`,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${COLORS.grey[400]}`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${COLORS.primary[400]}`,
            color: TEXT,
            background: "transparent",
            boxShadow: `0 0 0 3px ${alpha(COLORS.primary[400], 0.18)}`,
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${BORDER}`,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.error[500],
          },
          "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.error[600],
          },
          "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.error[500],
            boxShadow: `0 0 0 3px ${alpha(COLORS.error[500], 0.18)}`,
          },
          "& svg": { color: ICON },
        },
        input: {
          padding: "10px 12px",
          "::placeholder": {
            color: COLORS.grey[400],
            WebkitTextFillColor: COLORS.grey[400],
            opacity: "unset",
          },
          "&&:disabled": {
            color: TEXT_MUTED,
            WebkitTextFillColor: TEXT_MUTED,
            opacity: 1,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          lineHeight: "18px",
          marginLeft: 0,
          marginRight: 0,
          marginTop: 6,
          "&.Mui-error": { color: COLORS.error[500] },
        },
      },
    },

    MuiCard: {
      variants: [
        {
          props: {},
          style: {
            border: `1px solid ${BORDER}`,
            borderRadius: DD_RADIUS_LG,
            transition: "none",
            background: SURFACE_2,
            color: TEXT,
            ":hover": {
              border: `1px solid ${alpha(COLORS.primary[400], 0.6)}`,
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary[400], 0.16)}`,
            },
            ":focus": {
              border: `1px solid ${alpha(COLORS.primary[400], 0.6)}`,
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary[400], 0.16)}`,
            },
          },
        },
        {
          props: { variant: "glass" },
          style: {
            border: `1px solid ${alpha(COLORS.grey[700], 0.5)}`,
            borderRadius: "16px",
            background: alpha("#0F172A", 0.6),
            backgroundImage: "none",
            backdropFilter: "blur(12px)",
            color: TEXT,
            transition: "all 0.2s ease",
            ":hover": {
              background: alpha("#1E293B", 0.7),
              borderColor: alpha(COLORS.primary[500], 0.3),
              boxShadow: `0 0 20px ${alpha(COLORS.primary[500], 0.05)}`,
            },
            ":focus-within": {
              borderColor: alpha(COLORS.primary[500], 0.3),
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary[500], 0.12)}`,
            },
          },
        },
        {
          props: { variant: "dashed" },
          style: {
            padding: "16px",
            transition: "none",
            border: `1px dashed ${alpha(COLORS.primary[400], 0.8)}`,
            borderRadius: DD_RADIUS_LG,
            background: "none",
            ":hover": {
              backgroundColor: alpha(COLORS.primary[500], 0.08),
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary[400], 0.14)}, inset 0px 0px 20px 1px ${alpha(
                COLORS.primary[400],
                0.12,
              )}`,
            },
            ":active": {
              backgroundColor: alpha(COLORS.grey[200], 0.6),
              boxShadow: "none",
            },
            "&.disabled": {
              borderColor: BORDER,
              color: TEXT_MUTED,
              ":hover": {
                border: `1px dashed ${BORDER}`,
                backgroundColor: "unset",
                boxShadow: "unset",
              },
            },
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            padding: "20px 16px",
            border: `1px solid ${BORDER}`,
            borderRadius: DD_RADIUS_LG,
            background: SURFACE,
            ":hover": { backgroundColor: SURFACE_2 },
            ":focus": { backgroundColor: SURFACE_2 },
            ":active": { backgroundColor: alpha(COLORS.grey[200], 0.8) },
          },
        },
        {
          props: { variant: "elevation" },
          style: {
            padding: "18px 24px 18px 24px",
            cursor: "pointer",
            borderRadius: DD_RADIUS_LG,
            ":hover": { backgroundColor: HOVER },
            ":focus": { backgroundColor: HOVER },
            ":active": { backgroundColor: ACTIVE },
            "&.selected": {
              border: `1px solid ${alpha(COLORS.primary[400], 0.5)}`,
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary[400], 0.14)}`,
            },
            ":has(button:hover)": {
              backgroundColor: SURFACE,
              borderRadius: DD_RADIUS_LG,
              border: "1px solid transparent",
              boxShadow: "unset",
            },
          },
        },
      ],
    },

    MuiIconButton: {
      defaultProps: { disableRipple: true },
      variants: [
        {
          props: {},
          style: {
            fontSize: "24px",
            width: "32px",
            height: "32px",
            padding: "8px",
            borderRadius: DD_RADIUS_MD,
            color: ICON,
            ":hover": { backgroundColor: HOVER, color: TEXT },
            ":active": {
              backgroundColor: alpha(COLORS.primary[500], 0.12),
              color: COLORS.primary[700],
            },
            ":focus": {
              outline: "none",
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary[400], 0.18)}`,
            },
            ":disabled": { color: COLORS.grey[300] },
          },
        },
        {
          props: { size: "large" },
          style: { fontSize: "24px", width: "40px", height: "40px" },
        },
      ],
    },
    MuiSwipeableDrawer: {
      defaultProps: {
        slotProps: {
          backdrop: { sx: { backdropFilter: "blur(3px)" } },
        },
        PaperProps: {
          sx: {
            padding: "30px 16px 26px 16px",
            borderRadius: "16px 16px 0px 0px",
            borderRight: "none",
            borderTop: `1px solid ${COLORS.grey[700]}`,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: SURFACE,
          border: `1px solid ${BORDER}`,
          margin: "10px",
          padding: 0,
          width: "calc(100% - 32px)",
          maxHeight: "calc(100% - 32px)",
          borderRadius: "12px",
        },
        root: {
          backdropFilter: "blur(3px)",
          "& .MuiDialog-paperFullScreen": {
            margin: 0,
            width: "100%",
            maxHeight: "100%",
            borderRadius: "0px",
          },
          "& .MuiDialogContent-root .MuiDialogActions-root": {
            margin: "-24px -40px",
            marginTop: "40px",
          },
          "& .MuiDialog-paperFullScreen .MuiDialogTitle-root": {
            padding: "24px 16px",
          },
          "& .MuiDialog-paperFullScreen .MuiDialogContent-root": {
            padding: "0 16px 24px 16px",
          },
          "& .MuiDialog-paperFullScreen .MuiDialogActions-root": {
            backgroundColor: "unset",
            padding: "16px",
          },
          "& .MuiDialog-paperFullScreen .MuiDialogContent-root .MuiDialogActions-root":
            {
              padding: "16px 40px",
              marginTop: "24px",
            },
          "& .MuiDialog-paperFullScreen .MuiDialogActions-root .tooltipButtonWrapper":
            {
              display: "flex",
              flex: 1,
            },
          "& .MuiDialog-paperFullScreen .MuiDialogActions-root button": {
            flex: 1,
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "24px 40px 16px 40px",
          fontSize: typography.h2.fontSize,
          lineHeight: typography.h2.lineHeight,
          fontWeight: typography.fontWeightBold,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: "16px 40px 24px 40px" } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: SURFACE_2,
          borderTop: `1px solid ${BORDER}`,
          padding: "24px 40px",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: typography.h5.fontSize,
          lineHeight: typography.h5.lineHeight,
          fontWeight: 600,
          color: TEXT_MUTED,
          "&.Mui-focused": { color: COLORS.primary[400] },
          "&.Mui-error": { color: COLORS.error[500] },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: typography.h5.fontSize,
          lineHeight: typography.h5.lineHeight,
          fontWeight: 600,
          textAlign: "left",
          marginBottom: 8,
          color: TEXT_MUTED,
          "&.Mui-focused": { color: COLORS.primary[400] },
          "&.Mui-error": { color: COLORS.error[500] },
        },
      },
    },

    MuiMenu: {
      defaultProps: { disableAutoFocusItem: true, elevation: 2 },
      styleOverrides: {
        root: {
          "& ul > .MuiDivider-root": {
            backgroundColor: BORDER,
            color: TEXT,
            height: "2px",
            margin: "4px 0px 4px 0px",
          },
        },
        list: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        paper: {
          backgroundColor: SURFACE,
          borderRadius: DD_RADIUS_MD,
          border: `1px solid ${BORDER}`,
          padding: 0,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          "&:hover, &.Mui-focusVisible": {
            backgroundColor: alpha(COLORS.primary[500], 0.2),
            color: TEXT,
          },
          "&.Mui-selected": {
            backgroundColor: alpha(COLORS.primary[500], 0.14),
            color: TEXT,
          },
          "&.Mui-selected:hover": {
            backgroundColor: alpha(COLORS.primary[500], 0.24),
            color: TEXT,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: "unset" },
        flexContainer: { gap: "32px" },
        indicator: { backgroundColor: COLORS.primary[400] },
        scrollButtons: { "&.Mui-disabled": { opacity: 0.35 } },
      },
    },

    MuiTab: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          maxWidth: "fit-content",
          minHeight: "unset",
          paddingTop: "unset",
          paddingLeft: "unset",
          paddingRight: "unset",
          paddingBottom: "20px",
          fontSize: typography.h4.fontSize,
          lineHeight: typography.h4.lineHeight,
          color: COLORS.grey[500],
          fontWeight: typography.fontWeightLight,
          flexGrow: 1,
          "& svg": { fontSize: "20px" },
          "&&.Mui-selected": { color: COLORS.primary[400] },
          "&.Mui-disabled": { color: COLORS.grey[300] },
          ":hover": { color: COLORS.grey[300] },
          ":active": { color: TEXT },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { enterTouchDelay: 0, leaveTouchDelay: 300000 },
      styleOverrides: {
        tooltip: {
          color: COLORS.grey[100],
          backgroundColor: "#0B0F1A",
          border: `1px solid ${COLORS.grey[700]}`,
          borderRadius: DD_RADIUS_MD,
          margin: "15px",
          padding: "12px",
          fontSize: typography.h4.fontSize,
          fontWeight: "normal",
          lineHeight: typography.h4.lineHeight,
          maxWidth: "unset",
          "& .MuiTooltip-arrow": { color: COLORS.grey[700] },
        },
        popper: {
          "&[data-popper-placement*='top'] .MuiTooltip-tooltip": {
            marginBottom: 0,
          },
          "&[data-popper-placement*='bottom'] .MuiTooltip-tooltip": {
            marginTop: 0,
          },
        },
        arrow: { color: COLORS.grey[700] },
      },
    },

    MuiFab: {
      styleOverrides: {
        circular: {
          height: "48px",
          width: "48px",
          "& svg": { width: "20px", height: "20px", color: COLORS.grey[100] },
          ...buttons.variant.contained(COLORS.primary),
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          color: BADGE_TEXT_COLOR,
          backgroundColor: BADGE_COLOR,
          fontSize: typography.h6.fontSize,
          lineHeight: typography.h6.lineHeight,
          fontWeight: typography.fontWeightBold,
          border: `1px solid ${alpha(COLORS.grey[300], 0.15)}`,
        },
      },
    },
  },
};
